/**
 * Application Configuration
 * Loads and manages configuration from public/config.json
 */

import { apiClient, type AppConfig } from "./api/client";

let appConfig: AppConfig | null = null;
let configPromise: Promise<AppConfig> | null = null;

/**
 * Load application configuration from public/config.json
 * This function is safe to call multiple times - it will return the same promise
 */
export async function loadConfig(): Promise<AppConfig> {
  if (appConfig) {
    return appConfig;
  }

  if (!configPromise) {
    configPromise = apiClient.loadConfig();
  }

  appConfig = await configPromise;
  // Ensure we have the full config with all fields
  if (!appConfig.aiToken && appConfig.apiBaseUrl) {
    // If aiToken is missing, try reloading
    configPromise = apiClient.loadConfig();
    appConfig = await configPromise;
  }
  return appConfig;
}

/**
 * Get current configuration (synchronous)
 * Returns null if config hasn't been loaded yet
 */
export function getConfig(): AppConfig | null {
  return appConfig;
}

/**
 * Get API base URL
 */
export function getApiBaseUrl(): string {
  return appConfig?.apiBaseUrl || apiClient.getBaseURL();
}

/**
 * Get AI Token for ChatGPT
 */
export function getAiToken(): string | undefined {
  return appConfig?.aiToken;
}

/**
 * Get Payment Mode (sandbox or production)
 * Defaults to "sandbox" if not configured
 */
export function getPaymentMode(): "sandbox" | "production" {
  return appConfig?.paymentMode || "sandbox";
}

/**
 * Initialize configuration
 * Call this at app startup to ensure config is loaded before API calls
 */
export async function initConfig(): Promise<void> {
  await loadConfig();
}

